import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AtSign, Heart, MessageCircle, Quote as QuoteIcon, Check } from "lucide-react";
import { api } from "../lib/api";

const ICONS = {
  mention: AtSign,
  like: Heart,
  comment: MessageCircle,
  quote: QuoteIcon,
};
const TYPE_LABEL = {
  mention: "seni etiketledi",
  like: "gönderini beğendi",
  comment: "gönderine yorum yazdı",
  quote: "gönderini alıntıladı",
};

function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} g`;
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* skip silently */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const readAll = async () => {
    try {
      await api.put("/notifications/read-all");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const onClickItem = async (n) => {
    if (!n.read) {
      try { await api.put(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    navigate("/anilar?tab=gunluk");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((s) => !s)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-md transition hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--text-1))]"
        aria-label="Bildirimler"
        title="Bildirimler"
        data-testid="notifications-bell"
      >
        <Bell className="w-3.5 h-3.5" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full text-[9px] font-bold flex items-center justify-center px-0.5"
            style={{ backgroundColor: "#dc2626", color: "white", border: "1.5px solid white" }}
            data-testid="notifications-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-w-[90vw] tk-card overflow-hidden z-50"
          data-testid="notifications-dropdown"
          style={{ boxShadow: "0 12px 32px rgba(0,0,0,0.18)" }}
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: "hsl(var(--border))" }}>
            <div className="font-display font-semibold text-sm flex items-center gap-2"><Bell className="w-4 h-4" /> Bildirimler</div>
            {unread > 0 && (
              <button onClick={readAll} className="text-xs flex items-center gap-1 hover:underline" style={{ color: "#4992ff" }} data-testid="notifications-read-all">
                <Check className="w-3 h-3" /> Hepsini okundu yap
              </button>
            )}
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-xs" style={{ color: "hsl(var(--text-2))" }}>
                Henüz bildirim yok.
              </div>
            ) : items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id}
                  onClick={() => onClickItem(n)}
                  className={`w-full flex items-start gap-2.5 px-4 py-2.5 text-left hover:bg-[hsl(var(--surface-2))] transition ${!n.read ? "bg-[#fff7ee]" : ""}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: !n.read ? "#ff6b35" : "hsl(var(--surface-2))" }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: !n.read ? "white" : "hsl(var(--text-2))" }} />
                  </div>
                  <div className="flex-1 min-w-0 text-xs">
                    <div>
                      <b>{n.actor_display}</b>{" "}
                      <span style={{ color: "hsl(var(--text-2))" }}>{TYPE_LABEL[n.type] || n.type}</span>
                    </div>
                    {n.snippet && <div className="mt-0.5 line-clamp-2" style={{ color: "hsl(var(--text-2))" }}>"{n.snippet}"</div>}
                    <div className="mt-0.5" style={{ color: "hsl(var(--text-2))", fontSize: 10 }}>{fmtRel(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: "#ff6b35" }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
